import { http } from "./http";

/**
 * The API returns a root-relative logo path such as `/api/brand/logo?managerId=…`.
 * If that string ever picks up an extra leading slash (`//api/…`), browsers treat
 * it as a protocol-relative URL with host `api`, which becomes `http://api/…`
 * and fails DNS. Same for a mistaken absolute `http://api/…`. Normalise those
 * back to a single-root path.
 */
export function normalizeBrandLogoUrl(url: string | null | undefined): string | null {
  if (url == null || typeof url !== "string") return null;
  let t = url.trim();
  if (!t) return null;

  // Path-only "api/brand/..." (missing leading slash) — same-origin resolve is wrong.
  if (/^api\/brand\//i.test(t)) {
    return `/${t}`;
  }

  const lower = t.toLowerCase();
  if (lower.startsWith("http://api/") || lower.startsWith("https://api/")) {
    const afterHost = t.replace(/^https?:\/\/api/i, "");
    if (afterHost.toLowerCase().startsWith("/api")) return afterHost;
    return `/api${afterHost.startsWith("/") ? afterHost : `/${afterHost}`}`;
  }

  if (lower.startsWith("//api/")) {
    const rest = t.slice("//api/".length).replace(/^\/+/, "");
    return `/api/${rest}`;
  }

  if (t.startsWith("//")) {
    t = t.replace(/^\/+/, "/");
  }

  return t || null;
}

/**
 * Resolved brand for the current request, returned by GET /api/brand.
 * Anonymous callers see the system / host-resolved manager; authenticated
 * customers see their per-customer override (if any) layered on top of
 * the manager default.
 */
export interface Brand {
  managerId: number;
  /** Header wordmark shown next to the logo. Empty string means the
   * manager hasn't configured a wordmark — the TopBar renders only the
   * logo in that case. */
  portalName: string;
  primaryColor: string;
  logoUrl: string | null;
  /** What the browser tab title should read. Separate from portalName
   * because the tab must always have a value (falls back to the customer
   * name, then a neutral "Portal") whereas the header may legitimately
   * stay empty. */
  documentTitle: string;
}

export interface ManagerBrand {
  managerId: number;
  name: string;
  portalName: string;
  portalUrl: string;
  primaryColor: string;
  hasLogo: boolean;
  logoMimeType: string;
}

export interface ManagerBrandList {
  items: ManagerBrand[];
}

export const brandApi = {
  get: async () => {
    // On the public reset-password page the reset key selects the brand of
    // the login's customer (the visitor has no session yet).
    let query = "";
    if (typeof window !== "undefined" && window.location.pathname === "/reset-password") {
      const key = new URLSearchParams(window.location.search).get("key");
      if (key) query = `?resetKey=${encodeURIComponent(key)}`;
    }
    const b = await http<Brand>(`/api/brand${query}`);
    return {
      ...b,
      logoUrl: normalizeBrandLogoUrl(b.logoUrl),
    };
  },
  listManagers: () => http<ManagerBrandList>("/api/admin/brand/managers"),
  setPrimaryColor: (managerId: number, value: string) =>
    http<void>(
      `/api/admin/brand/managers/${managerId}/primary-color`,
      {
        method: "PUT",
        body: JSON.stringify({ value }),
      },
    ),
  setPortalName: (managerId: number, value: string) =>
    http<void>(
      `/api/admin/brand/managers/${managerId}/portal-name`,
      {
        method: "PUT",
        body: JSON.stringify({ value }),
      },
    ),
  uploadLogo: (managerId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`/api/admin/brand/managers/${managerId}/logo`, {
      method: "POST",
      credentials: "include",
      body: fd,
    }).then((r) => {
      if (!r.ok) {
        return r.text().then((t) => {
          throw new Error(t || `Upload failed (${r.status})`);
        });
      }
    });
  },
};
